import { ReadonlyVec3, vec2, vec3 } from 'gl-matrix';
import {
    ContourGeometricType,
    DicomContour,
} from '~/domain/dicom/dicomstructureset';
import { isPointInsidePolygon } from '~/engine/util/algorithms/sdf/polygonWinding';

export type PointMembership = (pointMm: ReadonlyVec3) => boolean;

const PLANE_TOLERANCE_MM = 0.05;

interface ContourPlane {
    readonly levelMm: number;
    readonly polygons: vec2[][];
    lowerMm: number;
    upperMm: number;
}

function planeNormal(points: ReadonlyArray<ReadonlyVec3>): vec3 | undefined {
    const normal = vec3.create();
    for (let i = 0; i < points.length; i++) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        normal[0] += (a[1] - b[1]) * (a[2] + b[2]);
        normal[1] += (a[2] - b[2]) * (a[0] + b[0]);
        normal[2] += (a[0] - b[0]) * (a[1] + b[1]);
    }
    return vec3.length(normal) > 0 ? vec3.normalize(normal, normal) : undefined;
}

function planeBasis(normal: ReadonlyVec3): readonly [vec3, vec3] {
    const seed =
        Math.abs(normal[0]) < 0.9
            ? vec3.fromValues(1, 0, 0)
            : vec3.fromValues(0, 1, 0);
    const u = vec3.cross(vec3.create(), normal, seed);
    vec3.normalize(u, u);
    return [u, vec3.cross(vec3.create(), normal, u)];
}

export function closedContourStackMembership(
    contours: ReadonlyArray<DicomContour>
): PointMembership | undefined {
    const closed = contours.filter(
        (contour) =>
            contour.type === ContourGeometricType.CLOSED_PLANAR &&
            contour.data.length >= 3
    );
    if (closed.length === 0) {
        return undefined;
    }
    const widest = closed.reduce((a, b) =>
        b.data.length > a.data.length ? b : a
    );
    const normal = planeNormal(widest.data);
    if (!normal) {
        return undefined;
    }
    const [u, v] = planeBasis(normal);

    const projected = closed
        .map((contour) => {
            let level = 0;
            for (const point of contour.data) {
                level += vec3.dot(point, normal);
            }
            return {
                levelMm: level / contour.data.length,
                polygon: contour.data.map((point) =>
                    vec2.fromValues(vec3.dot(point, u), vec3.dot(point, v))
                ),
            };
        })
        .sort((a, b) => a.levelMm - b.levelMm);

    const planes: ContourPlane[] = [];
    for (const entry of projected) {
        const open = planes[planes.length - 1];
        if (open && entry.levelMm - open.levelMm <= PLANE_TOLERANCE_MM) {
            open.polygons.push(entry.polygon);
        } else {
            planes.push({
                levelMm: entry.levelMm,
                polygons: [entry.polygon],
                lowerMm: 0,
                upperMm: 0,
            });
        }
    }
    if (planes.length < 2) {
        return undefined;
    }
    for (let i = 0; i < planes.length; i++) {
        const plane = planes[i];
        const midpointTo = (other: ContourPlane) =>
            (plane.levelMm + other.levelMm) / 2;
        // Outermost slabs extend half their neighbor gap beyond the contour.
        plane.upperMm =
            i < planes.length - 1
                ? midpointTo(planes[i + 1])
                : 2 * plane.levelMm - midpointTo(planes[i - 1]);
        plane.lowerMm =
            i > 0
                ? midpointTo(planes[i - 1])
                : 2 * plane.levelMm - midpointTo(planes[i + 1]);
    }

    const inPlane = vec2.create();
    return (pointMm) => {
        const level = vec3.dot(pointMm, normal);
        let low = 0;
        let high = planes.length - 1;
        while (low < high) {
            const mid = (low + high) >> 1;
            if (level > planes[mid].upperMm) {
                low = mid + 1;
            } else {
                high = mid;
            }
        }
        const plane = planes[low];
        if (level < plane.lowerMm || level > plane.upperMm) {
            return false;
        }
        vec2.set(inPlane, vec3.dot(pointMm, u), vec3.dot(pointMm, v));
        let inside = false;
        for (const polygon of plane.polygons) {
            if (isPointInsidePolygon(inPlane, polygon)) {
                inside = !inside;
            }
        }
        return inside;
    };
}
